import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin } from 'antd';
import { errHandler } from '../../helper/helper';
import { getLoanApplicationsProgressReport } from "./LoanService";
import generateLoanProgressExcel from '../../Utils/Loan/LoanApplicationProgressExcel';

const { Option } = Select;

const LoanApplicationProcess = ({
    loginPlaceId,
    districtData,
    districtName,
    loanProducts,
    divisionalId,
    branchId
}) => {
    const [loading, setLoading] = useState(false);
    
    const [filters, setFilters] = useState({ branches: [], products: [] });
    const [availableBranches, setAvailableBranches] = useState([]);

    useEffect(() => {
        let branches = [];
        const role = String(loginPlaceId);

        if (role === "1" && branchId) {
            const allBranches = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || []);
            const myBranch = allBranches.find(b => String(b.id) === String(branchId));
            if (myBranch) branches = [myBranch];
        } else if (role === "2" && divisionalId) {
            branches = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => String(div.divAId) === String(divisionalId))
                .flatMap(div => div.branchDtoList || []);
        } else if (role === "4" && districtName) {
            const myDistrict = districtData.find(d => d.districtName === districtName);
            if (myDistrict) {
                branches = (myDistrict.divisionalAreaDtoList || []).flatMap(div => div.branchDtoList || []);
            }
        } else {
            branches = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .flatMap(div => div.branchDtoList || []);
        }
        
        setAvailableBranches(branches);

        // Pre-select for branch level users
        if (role === "1" && branches.length > 0) {
            setFilters(prev => ({ ...prev, branches: [branches[0].id] }));
        }

    }, [districtData, loginPlaceId, branchId, divisionalId, districtName]);

    const generateReport = async () => {
        setLoading(true);

        try {
            const selectedBranchIds = String(loginPlaceId) === "1" ? [branchId] : filters.branches;

            const payload = {
                branchIdList: selectedBranchIds,
                productIdList: filters.products.length === 0 ? null : filters.products
            };

            const response = await getLoanApplicationsProgressReport(payload);
            console.log("API Response:", response);

            const rawData = Array.isArray(response) ? response : [];
            if (rawData.length === 0) {
                errHandler("No application records found");
                return;
            }

            let productHeader = "All Loan Types";
            if (filters.products.length === 1) {
                const prod = loanProducts.find(p => p.productId === filters.products[0]);
                productHeader = prod ? `${prod.productCode} - ${prod.productName}` : "Selected Type";
            } else if (filters.products.length > 1) {
                productHeader = `${filters.products.length} Types Selected`;
            }

            await generateLoanProgressExcel(rawData, productHeader);

        } catch (error) {
            console.error(error);
            errHandler(error?.message || "Failed to generate report");
        } finally {
            setLoading(false);
        }
    };

    const isGenerateDisabled = loading || (String(loginPlaceId) !== "1" && (!filters.branches || filters.branches.length === 0));

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={filters.branches}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setFilters(prev => ({
                                                ...prev,
                                                branches: availableBranches.map(b => b.id)
                                            }));
                                        } else {
                                            setFilters(prev => ({ ...prev, branches: values }));
                                        }
                                    }}
                                    disabled={availableBranches.length === 0 || String(loginPlaceId) === "1"}
                                    showSearch
                                    filterOption={(input, option) => {
                                        const children = option.children;
                                        if (typeof children === 'string') {
                                            return children.toLowerCase().includes(input.toLowerCase());
                                        }
                                        if (Array.isArray(children)) {
                                            return children.join('').toLowerCase().includes(input.toLowerCase());
                                        }
                                        return false;
                                    }}
                                >
                                    {String(loginPlaceId) !== "1" && (
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Branches
                                        </Option>
                                    )}
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={filters.products}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setFilters(prev => ({
                                                ...prev,
                                                products: loanProducts.map(b => b.productId)
                                            }));
                                        } else {
                                            setFilters(prev => ({ ...prev, products: values }));
                                        }
                                    }}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Products
                                    </Option>
                                    {loanProducts.map(product => (
                                        <Option key={product.productId} value={product.productId}>
                                            {product.productName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    type="primary"
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                    size="large"
                                    block
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default LoanApplicationProcess;
